import { useState } from 'react';
import { 
  Home, 
  Users, 
  BookOpen, 
  DollarSign, 
  Package, 
  Tag, 
  Settings, 
  BarChart3, 
  FileText, 
  Shield, 
  Bell, 
  Activity,
  Crown,
  HardDrive,
  Flag,
  TrendingUp,
  AlertTriangle,
  Handshake,
  Plus,
  Minus,
  Globe
} from 'lucide-react';

interface AdminSidebarProps {
  activeSection: string;
  setActiveSection?: (section: string) => void;
  onSectionChange?: (section: string) => void;
  systemType?: 'course' | 'salepage';
}

interface NavItem {
  id: string;
  label: string;
  icon: React.ElementType;
  badge?: string;
  subItems?: NavItem[];
  isExternal?: boolean;
  externalUrl?: string;
}

export default function AdminSidebar({ 
  activeSection, 
  setActiveSection,
  onSectionChange,
  systemType = 'course'
}: AdminSidebarProps) {
  const handleSectionChange = setActiveSection || onSectionChange || (() => {});
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  const navItems: NavItem[] = [
    {
      id: 'overview',
      label: 'ภาพรวม',
      icon: Home,
    },
    {
      id: 'users',
      label: 'ผู้ใช้',
      icon: Users,
      subItems: [
        {
          id: 'all-users',
          label: 'ผู้ใช้ทั้งหมด',
          icon: Users,
        },
        {
          id: 'instructors',
          label: 'ผู้สอน',
          icon: Crown,
        },
        {
          id: 'partners',
          label: 'Partners',
          icon: Handshake,
        },
      ],
    },
    {
      id: 'courses',
      label: 'คอร์ส',
      icon: BookOpen,
      subItems: [
        {
          id: 'all-courses',
          label: 'คอร์สทั้งหมด',
          icon: BookOpen,
        },
        {
          id: 'flagged-courses',
          label: 'คอร์สที่ถูกรายงาน',
          icon: Flag,
        },
      ],
    },
    {
      id: 'revenue',
      label: 'รายได้',
      icon: DollarSign,
      subItems: [
        {
          id: 'total-revenue',
          label: 'รายได้รวม',
          icon: DollarSign,
        },
        {
          id: 'subscription-payments',
          label: 'การชำระเงิน',
          icon: FileText,
        },
        {
          id: 'payment-approval',
          label: 'อนุมัติการชำระเงิน',
          icon: Shield,
        },
        {
          id: 'payment-settings',
          label: 'ตั้งค่าการชำระเงิน',
          icon: Settings,
        },
      ],
    },
    {
      id: 'management',
      label: 'การจัดการ',
      icon: Settings,
      subItems: [
        {
          id: 'subscription-management',
          label: 'จัดการ Subscription',
          icon: Crown,
        },
        {
          id: 'discount-management',
          label: 'จัดการส่วนลด',
          icon: Tag,
        },
        {
          id: 'custom-domains',
          label: 'Custom Domains',
          icon: Globe,
          isExternal: true,
          externalUrl: '/admin/custom-domains',
        },
        {
          id: 'storage-management',
          label: 'จัดการพื้นที่',
          icon: HardDrive,
        },
        {
          id: 'system-settings',
          label: 'ตั้งค่าระบบ',
          icon: Settings,
        },
      ],
    },
    {
      id: 'monitoring',
      label: 'การตรวจสอบ',
      icon: Activity,
      subItems: [
        {
          id: 'video-reports',
          label: 'รายงานปัญหาวิดีโอ',
          icon: Flag,
        },
        {
          id: 'activity-logs',
          label: 'บันทึกกิจกรรม',
          icon: Activity,
        },
        {
          id: 'system-health',
          label: 'สถานะระบบ',
          icon: Shield,
        },
        {
          id: 'notifications',
          label: 'การแจ้งเตือน',
          icon: Bell,
        },
      ],
    },
    {
      id: 'reports',
      label: 'รายงาน',
      icon: BarChart3,
      subItems: [
        {
          id: 'analytics',
          label: 'วิเคราะห์ข้อมูล',
          icon: TrendingUp,
        },
        {
          id: 'usage-stats',
          label: 'สถิติการใช้งาน',
          icon: BarChart3,
        },
      ],
    },
  ];

  const renderNavItem = (item: NavItem, level = 0) => {
    const isActive = activeSection === item.id || !!item.subItems?.some(sub => sub.id === activeSection);
    const hasSubItems = item.subItems && item.subItems.length > 0;
    const isExpanded = expandedSections.has(item.id);

    const itemBaseClass = 'relative w-full group';

    if (item.isExternal && item.externalUrl) {
      return (
        <div key={item.id} className={`${level > 0 ? 'ml-4' : ''}`}>
          <a
            href={item.externalUrl}
            className={`${itemBaseClass} flex items-center justify-between px-3 py-2.5 rounded-xl transition-all duration-200 active:scale-[0.99] text-gray-600 hover:bg-gray-50 hover:text-gray-900 ${level > 0 ? 'pl-3 pr-3 py-2' : ''}`}
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center transition-colors bg-transparent group-hover:bg-white/60">
                <item.icon className="w-5 h-5 text-gray-500" />
              </div>
              <span className="text-sm font-medium">{item.label}</span>
            </div>
          </a>
        </div>
      );
    }

    return (
      <div key={item.id} className={`${level > 0 ? 'ml-4' : ''}`}>
        <div className="flex items-center gap-1">
          <button
            onClick={() => {
              if (hasSubItems) {
                handleSectionChange(item.subItems![0].id);
              } else {
                handleSectionChange(item.id);
              }
            }}
            className={`${itemBaseClass} flex items-center justify-between px-3 py-2.5 rounded-xl transition-all duration-200 active:scale-[0.99] ${
              isActive
                ? 'bg-gradient-to-r from-blue-600/10 to-pink-600/5 text-blue-700 font-semibold'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
            } ${level > 0 ? 'pl-3 pr-3 py-2' : ''} ${hasSubItems ? 'flex-1' : ''}`}
          >
            {isActive && level === 0 && (
              <div className="absolute left-0 top-1/2 -translate-y-1/2 h-7 w-1.5 rounded-r-full bg-gradient-to-b from-blue-600 to-pink-600" />
            )}
            <div className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center transition-colors ${
                isActive ? 'bg-white/70 shadow-sm' : 'bg-transparent group-hover:bg-white/60'
              }`}>
                <item.icon className={`w-5 h-5 ${isActive ? 'text-blue-600' : 'text-gray-500'}`} />
              </div>
              <span className="text-sm font-medium">{item.label}</span>
            </div>
            {item.badge && (
              <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded-full">
                {item.badge}
              </span>
            )}
          </button>
          
          {hasSubItems && level === 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleSection(item.id);
              }}
              className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors"
            >
              {isExpanded ? (
                <Minus className="w-4 h-4 text-gray-600" />
              ) : (
                <Plus className="w-4 h-4 text-gray-600" />
              )}
            </button>
          )}
        </div>
        
        {hasSubItems && isExpanded && (
          <div className="mt-1 space-y-1">
            {item.subItems!.map(subItem => renderNavItem(subItem, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-72 h-full">
      <div className="sticky top-[84px]">
        <div className="rounded-2xl border border-white/40 bg-white/70 backdrop-blur-xl shadow-xl shadow-black/5 overflow-hidden">
          <div className="p-5 border-b border-gray-100/70">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 bg-gradient-to-br from-blue-600 to-pink-600 rounded-2xl flex items-center justify-center shadow-lg shadow-purple-500/20">
                <Crown className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-base font-bold text-gray-900 leading-tight">Admin</h2>
                <p className="text-xs text-gray-500">{systemType === 'course' ? 'Course System' : 'Salepage System'}</p>
              </div>
            </div>
          </div>

          <div className="p-3 max-h-[calc(100vh-140px)] overflow-y-auto">
            <nav className="space-y-1">
              {navItems.map(item => renderNavItem(item))}
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
}
